import { ref, computed } from 'vue'
import {
  fetchGuardrails,
  createGuardrail,
  updateGuardrail,
  deleteGuardrail,
  fetchGuardrailById,
} from '@/apis'
import type { IBackendGuardrail, IGuardrailCreateRequest, IGuardrailUpdateRequest } from '@/types'
import { GuardrailType } from '@/types'

// Local interface for the frontend
export interface IGuardrail {
  id: string
  name: string
  content: string
  type: 'description' | 'highlight'
  isActive: boolean
  priority: number
}

export const useGuardrailsStore = () => {
  // State
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const descriptionGuardrails = ref<IGuardrail[]>([])
  const highlightGuardrails = ref<IGuardrail[]>([])

  // Helper functions to convert between backend and frontend formats
  const convertBackendToLocalGuardrail = (backendGuardrail: IBackendGuardrail): IGuardrail => ({
    id: backendGuardrail.id,
    name: backendGuardrail.title,
    content: backendGuardrail.content,
    type: backendGuardrail.type as 'description' | 'highlight',
    isActive: backendGuardrail.isActive,
    priority: backendGuardrail.priority,
  })

  const convertLocalToBackendGuardrail = (
    localGuardrail: Omit<IGuardrail, 'id'>,
  ): IGuardrailCreateRequest => ({
    title: localGuardrail.name,
    content: localGuardrail.content,
    type:
      localGuardrail.type === 'description' ? GuardrailType.DESCRIPTION : GuardrailType.HIGHLIGHT,
    isActive: localGuardrail.isActive,
    priority: localGuardrail.priority,
  })

  // Actions
  const loadGuardrails = async () => {
    isLoading.value = true
    error.value = null

    try {
      // Fetch all guardrails
      const backendGuardrails = await fetchGuardrails()

      // Separate by type and convert to local format
      const backendDescriptionGuardrails = backendGuardrails.filter((g) => g.type === 'description')
      const backendHighlightGuardrails = backendGuardrails.filter((g) => g.type === 'highlight')

      descriptionGuardrails.value = backendDescriptionGuardrails.map(convertBackendToLocalGuardrail)
      highlightGuardrails.value = backendHighlightGuardrails.map(convertBackendToLocalGuardrail)
    } catch (apiError) {
      console.error('Failed to load guardrails from API:', apiError)
      error.value = 'Failed to load guardrails'
    } finally {
      isLoading.value = false
    }
  }

  const addGuardrail = async (guardrail: Omit<IGuardrail, 'id'>) => {
    isLoading.value = true
    error.value = null

    try {
      // Create guardrail via API
      const backendGuardrail = convertLocalToBackendGuardrail(guardrail)
      const createdGuardrail = await createGuardrail(backendGuardrail)
      const localGuardrail = convertBackendToLocalGuardrail(createdGuardrail)

      if (guardrail.type === 'description') {
        descriptionGuardrails.value.push(localGuardrail)
      } else {
        highlightGuardrails.value.push(localGuardrail)
      }

      return localGuardrail
    } catch (apiError) {
      console.error('Failed to create guardrail via API:', apiError)
      error.value = 'Failed to create guardrail'
      throw apiError
    } finally {
      isLoading.value = false
    }
  }

  const removeGuardrail = async (id: string, type: 'description' | 'highlight') => {
    isLoading.value = true
    error.value = null

    try {
      await deleteGuardrail(id)

      // Remove from local state
      if (type === 'description') {
        descriptionGuardrails.value = descriptionGuardrails.value.filter((g) => g.id !== id)
      } else {
        highlightGuardrails.value = highlightGuardrails.value.filter((g) => g.id !== id)
      }
    } catch (apiError) {
      console.error('Failed to delete guardrail via API:', apiError)
      error.value = 'Failed to delete guardrail'
      throw apiError
    } finally {
      isLoading.value = false
    }
  }

  const updateGuardrailById = async (id: string, updates: Partial<IGuardrail>) => {
    isLoading.value = true
    error.value = null

    try {
      const updateData: IGuardrailUpdateRequest = {}
      if (updates.name !== undefined) updateData.title = updates.name
      if (updates.content !== undefined) updateData.content = updates.content
      if (updates.isActive !== undefined) updateData.isActive = updates.isActive
      if (updates.priority !== undefined) updateData.priority = updates.priority

      const updatedBackendGuardrail = await updateGuardrail(id, updateData)
      const updatedLocalGuardrail = convertBackendToLocalGuardrail(updatedBackendGuardrail)

      // Update local state with API response
      const descGuardrailIndex = descriptionGuardrails.value.findIndex((g) => g.id === id)
      if (descGuardrailIndex !== -1) {
        descriptionGuardrails.value[descGuardrailIndex] = updatedLocalGuardrail
        return
      }

      const highlightGuardrailIndex = highlightGuardrails.value.findIndex((g) => g.id === id)
      if (highlightGuardrailIndex !== -1) {
        highlightGuardrails.value[highlightGuardrailIndex] = updatedLocalGuardrail
      }
    } catch (apiError) {
      console.error('Failed to update guardrail via API:', apiError)
      error.value = 'Failed to update guardrail'
      throw apiError
    } finally {
      isLoading.value = false
    }
  }

  // Initialize store
  loadGuardrails()

  return {
    // State
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    descriptionGuardrails: computed(() => descriptionGuardrails.value),
    highlightGuardrails: computed(() => highlightGuardrails.value),

    // Actions
    loadGuardrails,
    addGuardrail,
    removeGuardrail,
    updateGuardrail: updateGuardrailById,
  }
}
